'use client';

import { useEffect, useState } from 'react';
import { getInsights } from '@/lib/actions/insights';
import { Link } from '@/i18n/routing';
import {
  Plus,
  Pencil,
  FileText,
  ExternalLink,
  Star,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { DeleteInsightButton } from '@/components/admin/insights/DeleteInsightButton';
import { format } from 'date-fns';

export default function AdminInsightsPage() {
  const [insights, setInsights] = useState<Awaited<ReturnType<typeof getInsights>>>([]);

  useEffect(() => {
    getInsights().then(setInsights);
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy">Manage Insights</h1>
          <p className="text-muted-foreground mt-1">Write and publish articles or career tips.</p>
        </div>
        <Button asChild className="bg-primary hover:bg-navy text-primary-foreground font-semibold">
          <Link href="/admin/insights/new">
            <Plus className="mr-2 h-4 w-4" /> New Article
          </Link>
        </Button>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Publish Date</TableHead>
              <TableHead>Featured</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {insights.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center py-12 text-gray-500">
                  <div className="flex flex-col items-center gap-2">
                    <FileText size={40} className="text-gray-300" />
                    <p>No articles found. Start writing!</p>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              insights.map((article) => (
                <TableRow key={article.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <span className="font-semibold">{article.title}</span>
                      <span className="text-xs text-gray-400">{article.slug}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="whitespace-nowrap">
                      {article.serviceCategory?.name || 'Uncategorized'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-gray-600">
                    {format(new Date(article.publishDate), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {article.isFeatured && (
                      <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="icon" asChild title="View on Site">
                        <Link href={`/insights/${article.slug}`} target="_blank">
                          <ExternalLink className="h-4 w-4 text-gray-500" />
                        </Link>
                      </Button>
                      <Button variant="ghost" size="icon" asChild title="Edit">
                        <Link href={`/admin/insights/${article.id}`}>
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Link>
                      </Button>
                      <DeleteInsightButton id={article.id} />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
